import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Loader2, BarChart3, TrendingUp, DollarSign, Users } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, periodsRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/payroll-periods`),
      ]);
      setStats(statsRes.data);
      setPeriods(periodsRes.data);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const CHART_COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
  ];

  // Prepare data for status distribution
  const statusCounts = periods.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  const statusData = Object.entries(statusCounts).map(([status, count]) => ({
    name: status.charAt(0).toUpperCase() + status.slice(1),
    value: count,
  }));

  // Calculate totals across all periods
  const totalGross = periods.reduce((sum, p) => sum + (p.total_gross || 0), 0);
  const totalSSB = periods.reduce((sum, p) => sum + (p.total_ssb_employee || 0) + (p.total_ssb_employer || 0), 0);
  const totalNet = periods.reduce((sum, p) => sum + (p.total_net || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Reports"
        subtitle="Payroll analytics and insights"
      />

      <div className="flex-1 p-4 lg:p-8 space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-base animate-slide-up" style={{ animationDelay: '0ms' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Gross (All Time)</p>
                  <p className="text-xl font-bold font-heading">{formatCurrency(totalGross)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base animate-slide-up" style={{ animationDelay: '50ms' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total SSB (All Time)</p>
                  <p className="text-xl font-bold font-heading">{formatCurrency(totalSSB)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base animate-slide-up" style={{ animationDelay: '100ms' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Net (All Time)</p>
                  <p className="text-xl font-bold font-heading">{formatCurrency(totalNet)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base animate-slide-up" style={{ animationDelay: '150ms' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Periods</p>
                  <p className="text-xl font-bold font-heading">{periods.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Weekly Payroll Trend */}
          <Card className="card-base animate-slide-up" style={{ animationDelay: '200ms' }}>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Payroll Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {stats?.weekly_totals?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.weekly_totals}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `$${value}`} />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '0.5rem',
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="gross" name="Gross Pay" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="net" name="Net Pay" stroke={CHART_COLORS[2]} strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="ssb" name="SSB" stroke={CHART_COLORS[1]} strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Period Status Distribution */}
          <Card className="card-base animate-slide-up" style={{ animationDelay: '250ms' }}>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-secondary" />
                Period Status Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payroll Comparison */}
        <Card className="card-base animate-slide-up" style={{ animationDelay: '300ms' }}>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-accent" />
              Gross vs Net vs SSB by Period
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[350px]">
              {periods.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={periods.slice(0, 10).reverse().map(p => ({
                    ...p,
                    total_ssb: (p.total_ssb_employee || 0) + (p.total_ssb_employer || 0),
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `$${value}`} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '0.5rem',
                      }}
                    />
                    <Legend />
                    <Bar dataKey="total_gross" name="Gross" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="total_net" name="Net" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="total_ssb" name="SSB" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No payroll data yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
